import { useEffect, useRef } from 'react';
import {
  matchPath,
  useLocation,
  useRoutes,
  type Location,
} from 'react-router-dom';

import { useUIStore } from '@/app/model';
import { modalRoutes, prefetchRoutes, routes } from './routes';

/**
 * Пути, которые открываются модалкой-оверлеем поверх фоновой страницы: детальная
 * задача и карточка проекта. Список синхронизирован с modalRoutes (routes.tsx).
 */
function isModalRoute(pathname: string): boolean {
  return (
    pathname === '/tasks-new' ||
    pathname.startsWith('/tasks-new/') ||
    // Только карточка задачи v2: сам список /tasks-v2 — обычная страница.
    matchPath('/tasks-v2/:id', pathname) != null ||
    matchPath('/projects/:entityTypeId/:id', pathname) != null
  );
}

// Сколько модальных слоёв поддерживаем поверх фона. В приложении встречается
// ровно один случай вложенности — задача, открытая из уже открытой карточки
// проекта (или наоборот), — под него и рассчитан лимит. Глубже стек не растёт:
// следующий модальный переход просто станет новым фоном (без анимации оверлея).
const MODAL_STACK_DEPTH = 3; // фон + 2 вложенных модальных слоя

/**
 * Детальные страницы задачи (`/tasks-new/:id`) и проекта
 * (`/projects/:entityTypeId/:id`) открываются модалкой поверх той страницы, с
 * которой их открыли: список с настроенными фильтрами, дашборд, карточка задачи
 * и т.п. Фон остаётся смонтированным — фильтры, скролл и состояние сохраняются, а
 * закрытие (крестик, «назад» в UI или в браузере) возвращает к нему без
 * перезагрузки, откуда бы страница ни была открыта. Поддерживается и вложенный
 * случай — задача, открытая изнутри уже открытой карточки проекта (и наоборот):
 * нижний модальный слой остаётся смонтированным (просто скрытым) под верхним, а
 * не размонтируется.
 *
 * Как: держим СТЕК локаций (см. MODAL_STACK_DEPTH). Нулевой элемент — обычная
 * (не модальная) локация, из которой стек начался; каждый следующий — вложенный
 * модальный переход. При «назад»/«вперёд» на уже виденный уровень стек не
 * пересоздаётся, а обрезается до него — ссылки на локации нижних уровней не
 * меняются, поэтому React их не перемонтирует. При прямом заходе/перезагрузке на
 * модальный путь стек стартует с него же — страница открывается обычной
 * (fallback через маршрут в routes), фона нет.
 *
 * Пока верхний оверлей открыт, все слои под ним прячем через `visibility:
 * hidden` (не unmount — состояние и скролл сохраняются). Это убирает fixed-
 * элементы нижних слоёв (навбар z-40, sticky-шапки z-60) из отрисовки, поэтому
 * верхний оверлей держится на низком «страничном» z-40 и НЕ перекрывает
 * глобальные модалки (они портятся в body с z 50…9999 и открываются поверх).
 */
export function AppRouter() {
  const location = useLocation();
  const isModal = isModalRoute(location.pathname);

  const stackRef = useRef<Location[]>([location]);
  const sameLoc = (a: Location, b: Location) =>
    a.pathname === b.pathname && a.search === b.search;
  const existingIdx = stackRef.current.findIndex((l) => sameLoc(l, location));
  if (existingIdx !== -1) {
    // «Назад»/«вперёд» на уже виденный уровень — обрезаем стек до него, ссылки
    // на оставшиеся локации не трогаем (см. комментарий класса выше).
    stackRef.current = stackRef.current.slice(0, existingIdx + 1);
  } else if (isModal && stackRef.current.length < MODAL_STACK_DEPTH) {
    stackRef.current = [...stackRef.current, location];
  } else {
    stackRef.current = [location];
  }
  const stack = stackRef.current;
  const background = stack[0];
  const modalLayer1 = stack[1] ?? null;
  const modalLayer2 = stack[2] ?? null;

  // Фон и нижний модальный слой остаются смонтированными и рендерятся по своей
  // (уже не текущей) локации, поэтому их useLocation видит её, а не реальный
  // URL. Прокидываем факт открытого оверлея через стор, чтобы фоновые
  // body-порталы (FAB «Создать») его учитывали.
  const setIsDetailOverlayOpen = useUIStore(
    (state) => state.setIsDetailOverlayOpen
  );
  useEffect(() => {
    setIsDetailOverlayOpen(modalLayer1 != null);
  }, [modalLayer1, setIsDetailOverlayOpen]);

  // После первого рендера, в простое браузера, прогреваем чанки остальных
  // страниц, чтобы переходы были мгновенными без полноэкранного лоадера
  // (см. prefetchRoutes). Не блокирует текущую страницу.
  useEffect(() => {
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(() => prefetchRoutes());
      return () => window.cancelIdleCallback?.(id);
    }
    const id = window.setTimeout(prefetchRoutes, 1500);
    return () => window.clearTimeout(id);
  }, []);

  // Три фиксированных вызова (правило хуков не даёт делать их по числу слоёв в
  // стеке) — незанятые слои рендерятся по фону и просто не выводятся ниже.
  const mainElement = useRoutes(routes, background);
  const layer1Element = useRoutes(modalRoutes, modalLayer1 ?? background);
  const layer2Element = useRoutes(modalRoutes, modalLayer2 ?? background);

  return (
    <>
      {/* Обёртка всегда одна и та же (mainElement не перемонтируется при
          открытии/закрытии оверлеев) — прячем её только по visibility. */}
      <div style={modalLayer1 ? { visibility: 'hidden' } : undefined}>
        {mainElement}
      </div>
      {modalLayer1 && (
        <div style={modalLayer2 ? { visibility: 'hidden' } : undefined}>
          {layer1Element}
        </div>
      )}
      {modalLayer2 && layer2Element}
    </>
  );
}
